import { Icon, Link, Stack, Tooltip, Typography } from '@mui/material';
import { Attention2Icon } from '@schooly/style';
import { formatListWithAnd } from '@schooly/utils/common';
import { FC, forwardRef, HTMLAttributes } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { TimetableWarningItem } from '../../SchoolGeneral/SchoolGeneralAgeGroups/am-pm-helpers';
import {
  TimetableCellContent,
  TimetableGroupEntry,
  TimetableRef,
} from './attendance-frequencies-helpers';

const WARNING_PART_MESSAGE_ID: Record<TimetableWarningItem, string> = {
  am: 'attendance-Frequencies-Warning-Part-Am',
  pm: 'attendance-Frequencies-Warning-Part-Pm',
  per_period: 'attendance-Frequencies-Warning-Part-PerPeriod',
};

const openTimetable = (id: string) => {
  window.open(`/settings/timetables/${id}`, '_blank');
};

const compactLabel = (timetables: TimetableRef[]): string => {
  const [first, ...rest] = timetables;
  return rest.length ? `${first.name}, +${rest.length}` : first.name;
};

const distinctTimetables = (entries: TimetableGroupEntry[]): TimetableRef[] => {
  const distinct = new Map<string, TimetableRef>();
  for (const entry of entries) {
    for (const timetable of entry.timetables) distinct.set(timetable.id, timetable);
  }
  return [...distinct.values()];
};

// forwardRef + prop spread so the element can act as a MUI Tooltip trigger:
// Tooltip clones its child and injects a ref and hover handlers, which must
// land on a real DOM node for the tooltip to open.
type CompactLinkProps = HTMLAttributes<HTMLSpanElement> & { timetables: TimetableRef[] };

const CompactLink = forwardRef<HTMLSpanElement, CompactLinkProps>(
  ({ timetables, ...props }, ref) => {
    if (!timetables.length) return null;
    return (
      <Typography ref={ref} component="span" sx={{ display: 'inline-block' }} {...props}>
        <Link
          underline="hover"
          onClick={() => openTimetable(timetables[0].id)}
          sx={{ textAlign: 'left', '&:hover': { color: 'primary.main' } }}
        >
          {compactLabel(timetables)}
        </Link>
      </Typography>
    );
  },
);

const TooltipLink: FC<{ timetable: TimetableRef }> = ({ timetable }) => (
  <Link
    component="button"
    color="inherit"
    underline="always"
    onClick={() => openTimetable(timetable.id)}
    sx={{ alignSelf: 'flex-start' }}
  >
    {timetable.name}
  </Link>
);

const EntryList: FC<{ entries: TimetableGroupEntry[] }> = ({ entries }) => (
  <Stack gap={1}>
    {entries.map((entry) => (
      <Stack key={entry.label} gap={0.25}>
        <Typography>{entry.label}:</Typography>
        {entry.timetables.map((timetable) => (
          <TooltipLink key={timetable.id} timetable={timetable} />
        ))}
      </Stack>
    ))}
  </Stack>
);

interface Props {
  cell: TimetableCellContent;
}

export const AttendanceFrequencyTimetableCell: FC<Props> = ({ cell }) => {
  const { formatMessage } = useIntl();

  if (cell.kind === 'not_required') {
    return (
      <Typography color="text.secondary">
        <FormattedMessage id="attendance-Frequencies-NotRequired" />
      </Typography>
    );
  }

  if (cell.kind === 'uniform') {
    if (!cell.timetables.length) return null;
    const trigger = <CompactLink timetables={cell.timetables} />;
    if (cell.timetables.length < 2) return trigger;

    return (
      <Tooltip
        title={
          <Stack gap={0.5} sx={{ p: 0.5 }}>
            {cell.timetables.map((timetable) => (
              <TooltipLink key={timetable.id} timetable={timetable} />
            ))}
          </Stack>
        }
      >
        {trigger}
      </Tooltip>
    );
  }

  if (cell.kind === 'per_group') {
    const distinct = distinctTimetables(cell.entries);
    if (!distinct.length) return null;

    return (
      <Tooltip
        title={
          <Stack sx={{ p: 0.5 }}>
            <EntryList entries={cell.entries} />
          </Stack>
        }
      >
        <CompactLink timetables={distinct} />
      </Tooltip>
    );
  }

  const [firstMissing, ...restMissing] = cell.missingLabels;
  const registerItems = formatListWithAnd(
    cell.registers.map((item) => formatMessage({ id: WARNING_PART_MESSAGE_ID[item] })),
    formatMessage,
  );

  return (
    <Tooltip
      title={
        <Stack gap={1} sx={{ p: 0.5 }}>
          <Typography>
            <FormattedMessage
              id="attendance-Frequencies-Warning-Tooltip-Registers"
              values={{
                ageGroups: cell.missingLabels.map((label) => `"${label}"`).join(', '),
                items: registerItems,
                count: cell.registers.length,
              }}
            />
          </Typography>
          {cell.coveredEntries.length > 0 && <EntryList entries={cell.coveredEntries} />}
        </Stack>
      }
    >
      <Stack direction="row" gap={0.5} alignItems="center">
        <Icon
          fontSize="small"
          sx={{
            color: 'error.main',
            '& .svg-icon': { '& circle, & rect': { color: 'common.white' } },
          }}
        >
          <Attention2Icon />
        </Icon>
        <Typography>
          <FormattedMessage
            id="attendance-Frequencies-NoTimetableFor"
            values={{ ageGroup: firstMissing }}
          />
          {restMissing.length ? `, +${restMissing.length}` : ''}
        </Typography>
      </Stack>
    </Tooltip>
  );
};
